import { useMemo, useState } from "react"

import { formatDistanceToNow } from "date-fns"
import { useTranslation } from "react-i18next"

import type { CanvasVersion } from "@/components/canvas-editor/version-history-dropdown"

import { ArrowLeft, Clock, RotateCcw, Sparkles, X } from "@/components/icons"
import {
  calculateContextDiff,
  identifyChangedLines,
} from "@/components/text-enhancement/diff-utils"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { STATUS_TONES } from "@/components/ui/tone-classes"
import { useDateLocale } from "@/hooks/use-date-locale"
import { cn } from "@/lib/utils"

type CanvasHistoryPanelProps = {
  isOpen: boolean
  isPhone?: boolean
  canRestore?: boolean
  versions: CanvasVersion[]
  currentContent: string
  onClose: () => void
  onRestoreVersion: (version: CanvasVersion) => void
}

export function CanvasHistoryPanel({
  isOpen,
  isPhone = false,
  canRestore = true,
  versions,
  currentContent,
  onClose,
  onRestoreVersion,
}: CanvasHistoryPanelProps) {
  const { t } = useTranslation()
  const dateLocale = useDateLocale()
  const [selectedVersion, setSelectedVersion] = useState<CanvasVersion | null>(null)

  const sortedVersions = useMemo(
    () => [...versions].sort((a, b) => b.version - a.version),
    [versions]
  )

  const diffResult = useMemo(() => {
    if (!selectedVersion) return null
    const diff = calculateContextDiff(selectedVersion.content, currentContent, 5)
    const changedLines = identifyChangedLines(diff.originalContext, diff.modifiedContext)
    return { ...diff, ...changedLines }
  }, [selectedVersion, currentContent])

  if (!isOpen) return null

  const handleRestore = () => {
    if (selectedVersion) {
      onRestoreVersion(selectedVersion)
      setSelectedVersion(null)
    }
  }

  const handleBack = () => {
    if (selectedVersion) {
      setSelectedVersion(null)
    } else {
      onClose()
    }
  }

  function VersionListItem({ version }: { version: CanvasVersion }) {
    return (
      <button
        type="button"
        className={cn(
          "hover:bg-accent w-full rounded-lg p-3 text-left transition-colors",
          selectedVersion?.version === version.version && "bg-accent text-accent-foreground"
        )}
        onClick={() => setSelectedVersion(version)}
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">
            {t("canvas.version", "Version")} {version.version}
          </span>
          {version.is_original && (
            <Badge className="text-xs" variant="secondary">
              {t("canvas.original", "Original")}
            </Badge>
          )}
          {version.is_ai_edit && (
            <Badge className="text-xs" variant="outline">
              <Sparkles className="mr-1 size-3" />
              {t("canvas.ai", "AI")}
            </Badge>
          )}
        </div>
        {version.ai_edit_preview && (
          <p className="text-muted-foreground mt-1 truncate text-xs italic">
            "{version.ai_edit_preview}"
          </p>
        )}
        <p className="text-muted-foreground mt-1 text-xs">
          {formatDistanceToNow(new Date(version.timestamp), {
            addSuffix: true,
            locale: dateLocale,
          })}
        </p>
      </button>
    )
  }

  function DiffLines({
    lines,
    changedLines,
    variant,
  }: {
    lines: string
    changedLines: Set<number>
    variant: "old" | "new"
  }) {
    return (
      <div className="space-y-0.5 font-mono text-xs sm:text-sm">
        {diffResult?.hasLinesBefore && (
          <div className="text-muted-foreground text-xs">
            ... {diffResult.linesBefore} {t("canvas.lines_above", "lines above")}
          </div>
        )}
        {lines.split("\n").map((line, index) => (
          <div
            key={index}
            className={cn(
              "rounded px-2 py-0.5 whitespace-pre-wrap",
              changedLines.has(index) &&
                (variant === "old" ? STATUS_TONES.destructive.soft : STATUS_TONES.success.soft)
            )}
          >
            {line || " "}
          </div>
        ))}
        {diffResult?.hasLinesAfter && (
          <div className="text-muted-foreground text-xs">
            ... {diffResult.linesAfter} {t("canvas.lines_below", "lines below")}
          </div>
        )}
      </div>
    )
  }

  if (isPhone) {
    return (
      <div className="bg-background animate-in slide-in-from-right-4 absolute inset-0 z-20 flex flex-col duration-200">
        {/* Header */}
        <div className="bg-card flex items-center justify-between border-b px-3 py-3">
          <div className="flex items-center gap-2">
            <Button className="size-9" size="icon" variant="ghost" onClick={handleBack}>
              <ArrowLeft className="size-4" />
            </Button>
            <div className="flex items-center gap-2">
              <Clock className="text-muted-foreground size-4" />
              <h3 className="font-semibold">
                {selectedVersion
                  ? t("canvas.comparing_version", "Comparing Version {{version}}", {
                      version: selectedVersion.version,
                    })
                  : t("canvas.version_history", "Version History")}
              </h3>
            </div>
          </div>
          {!selectedVersion && (
            <Button className="size-9" size="icon" variant="ghost" onClick={onClose}>
              <X className="size-4" />
            </Button>
          )}
        </div>

        {/* Content */}
        {selectedVersion ? (
          <div className="flex min-h-0 flex-1 flex-col">
            {/* Restore button */}
            {canRestore && (
              <div className="bg-card border-b px-3 py-2">
                <Button className="w-full" variant="outline" onClick={handleRestore}>
                  <RotateCcw className="mr-2 size-4" />
                  {t("canvas.restore_this_version", "Restore this version")}
                </Button>
              </div>
            )}

            {/* Diff with tabs */}
            {diffResult && (
              <Tabs className="flex min-h-0 flex-1 flex-col" defaultValue="old">
                <div className="bg-card border-b px-3 py-2">
                  <TabsList className="w-full">
                    <TabsTrigger className="flex-1" value="old">
                      {t("canvas.version", "Version")} {selectedVersion.version}
                    </TabsTrigger>
                    <TabsTrigger className="flex-1" value="new">
                      {t("canvas.current_version", "Current")}
                    </TabsTrigger>
                  </TabsList>
                  <p className="text-muted-foreground mt-2 text-center text-xs">
                    {t("canvas.diff_description", "Red = removed, Green = added")}
                  </p>
                </div>
                <ScrollArea className="flex-1">
                  <TabsContent className="m-0 p-3" value="old">
                    <div className="bg-card text-card-foreground rounded-lg p-3">
                      <DiffLines
                        changedLines={diffResult.changedOriginalLines}
                        lines={diffResult.originalContext}
                        variant="old"
                      />
                    </div>
                  </TabsContent>
                  <TabsContent className="m-0 p-3" value="new">
                    <div className="bg-card text-card-foreground rounded-lg p-3">
                      <DiffLines
                        changedLines={diffResult.changedModifiedLines}
                        lines={diffResult.modifiedContext}
                        variant="new"
                      />
                    </div>
                  </TabsContent>
                </ScrollArea>
              </Tabs>
            )}
          </div>
        ) : (
          <ScrollArea className="flex-1">
            <div className="space-y-1 p-3">
              {sortedVersions.map((version) => (
                <VersionListItem key={version.version} version={version} />
              ))}
            </div>
          </ScrollArea>
        )}
      </div>
    )
  }

  return (
    <div className="animate-in slide-in-from-right-4 absolute inset-0 z-20 flex duration-200">
      <div className="bg-muted/40 flex min-h-0 flex-1">
        {/* Version list sidebar */}
        <div className="bg-card flex w-72 shrink-0 flex-col border-r">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <div className="flex items-center gap-2">
              <Clock className="text-muted-foreground size-4" />
              <h3 className="font-semibold">{t("canvas.version_history", "Version History")}</h3>
            </div>
            <Button size="icon" variant="ghost" onClick={onClose}>
              <X className="size-4" />
            </Button>
          </div>
          <ScrollArea className="flex-1">
            <div className="space-y-1 p-2">
              {sortedVersions.map((version) => (
                <VersionListItem key={version.version} version={version} />
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Diff view area */}
        <div className="flex min-w-0 flex-1 flex-col">
          {selectedVersion ? (
            <>
              <div className="bg-card flex items-center justify-between border-b px-6 py-3">
                <div className="flex items-center gap-3">
                  <Button size="icon" variant="ghost" onClick={() => setSelectedVersion(null)}>
                    <ArrowLeft className="size-4" />
                  </Button>
                  <div>
                    <h4 className="font-medium">
                      {t("canvas.comparing_version", "Comparing Version {{version}}", {
                        version: selectedVersion.version,
                      })}
                    </h4>
                    <p className="text-muted-foreground text-xs">
                      {t("canvas.diff_description", "Red = removed, Green = added")}
                    </p>
                  </div>
                </div>
                {canRestore && (
                  <Button variant="outline" onClick={handleRestore}>
                    <RotateCcw className="mr-2 size-4" />
                    {t("canvas.restore_this_version", "Restore this version")}
                  </Button>
                )}
              </div>
              <ScrollArea className="flex-1 p-6">
                {diffResult && (
                  <div className="bg-card text-card-foreground mx-auto max-w-4xl rounded-xl shadow-sm">
                    <div className="grid grid-cols-2 divide-x">
                      {/* Old version */}
                      <div className="p-4">
                        <h5 className="text-muted-foreground mb-3 text-xs font-semibold uppercase">
                          {t("canvas.version", "Version")} {selectedVersion.version}
                        </h5>
                        <DiffLines
                          changedLines={diffResult.changedOriginalLines}
                          lines={diffResult.originalContext}
                          variant="old"
                        />
                      </div>
                      {/* Current version */}
                      <div className="p-4">
                        <h5 className="text-muted-foreground mb-3 text-xs font-semibold uppercase">
                          {t("canvas.current_version", "Current")}
                        </h5>
                        <DiffLines
                          changedLines={diffResult.changedModifiedLines}
                          lines={diffResult.modifiedContext}
                          variant="new"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </ScrollArea>
            </>
          ) : (
            <div className="text-muted-foreground flex flex-1 items-center justify-center">
              <div className="text-center">
                <Clock className="mx-auto mb-3 size-12 opacity-30" />
                <p>{t("canvas.select_version_to_compare", "Select a version to compare")}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
